"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STATISTICS_REQUIRED_EVENT_TYPES = exports.STATISTICS_EVENT_SOURCES = exports.STATISTICS_REMOVED_MEMBER_SUFFIX = exports.STATISTICS_NO_SCREEN_LABEL = exports.STATISTICS_NO_COMMITTENTE_LABEL = exports.STATISTICS_NO_ASSIGNEE_LABEL = exports.STATISTICS_DISTRIBUTION_LABELS = exports.STATISTICS_DISTRIBUTIONS = exports.statisticsQuerySchema = exports.STATISTICS_TIME_ZONE = exports.STATISTICS_PERIOD_LABELS = exports.STATISTICS_PERIOD_DEFAULT = exports.STATISTICS_PERIODS = void 0;
exports.isStatisticsPeriod = isStatisticsPeriod;
const zod_1 = require("zod");
/**
 * Statistiche del servizio (PRD-005 US-2, story #11). Le definizioni degli indicatori sono qui,
 * accanto al contratto, e in PAGE-009: la vista mostra solo misure calcolate dagli istanti
 * registrati (DEC-002 regola 1), mai valori dimostrativi; un dato che manca è `null`.
 *
 * **Coorte**: i ticket **ricevuti nel periodo** (`received_at` dal primo istante del primo giorno,
 * mezzanotte Europe/Rome, fino all'istante della richiesta compreso). Tempi, quote e riaperture riguardano
 * quei ticket, con gli eventi registrati fino all'istante della richiesta.
 */
/** Periodi selezionabili, in giorni di calendario (prototipo: 7, 30, 90). */
exports.STATISTICS_PERIODS = [7, 30, 90];
exports.STATISTICS_PERIOD_DEFAULT = 30;
exports.STATISTICS_PERIOD_LABELS = {
    7: '7 giorni',
    30: '30 giorni',
    90: '90 giorni',
};
/** Fuso dei confini del periodo: quello del supporto e dei committenti. */
exports.STATISTICS_TIME_ZONE = 'Europe/Rome';
/** Vero se il valore (es. dalla query string della web app) è un periodo ammesso. */
function isStatisticsPeriod(value) {
    return exports.STATISTICS_PERIODS.includes(value);
}
/**
 * Query string di `GET /statistiche`: solo `periodo` (default 30). Parametri sconosciuti sono
 * rifiutati (400): **nessuna distribuzione né filtro per prodotto** (DEC-003).
 */
exports.statisticsQuerySchema = zod_1.z.strictObject({
    periodo: zod_1.z
        .enum(['7', '30', '90'], {
        error: `Periodo non valido: ${exports.STATISTICS_PERIODS.join(', ')} giorni`,
    })
        .default(`${exports.STATISTICS_PERIOD_DEFAULT}`)
        .transform((value) => Number(value)),
}, {
    error: (issue) => issue.code === 'unrecognized_keys' ? `Parametri non ammessi: ${issue.keys.join(', ')}` : undefined,
});
/** Le distribuzioni ammesse: nessuna per prodotto (DEC-003) né per canale paziente (DEC-005). */
exports.STATISTICS_DISTRIBUTIONS = ['channel', 'screen', 'assignee', 'committente'];
exports.STATISTICS_DISTRIBUTION_LABELS = {
    channel: { title: 'Da dove arrivano', subtitle: 'per canale di provenienza' },
    screen: { title: 'Schermate più segnalate', subtitle: 'dove si rompe' },
    assignee: { title: 'Per responsabile', subtitle: 'responsabile attuale del ticket' },
    committente: { title: 'Per committente', subtitle: 'rendicontazione' },
};
/** Etichetta dei ticket senza responsabile. */
exports.STATISTICS_NO_ASSIGNEE_LABEL = 'Non assegnato';
/** Etichetta dei ticket senza committente (pre-accesso, DEC-006: mai dedotto dal contenuto). */
exports.STATISTICS_NO_COMMITTENTE_LABEL = 'Committente non identificato';
/** Etichetta dei ticket senza schermata trasmessa. */
exports.STATISTICS_NO_SCREEN_LABEL = 'Schermata non indicata';
/** Suffisso del responsabile uscito dal team (rimozione logica: il nome resta). */
exports.STATISTICS_REMOVED_MEMBER_SUFFIX = ' (non più nel team)';
/**
 * **Vincolo strutturale** (PRD-005 US-2, DEC-002 regola 1): ciò che le statistiche leggono e dove
 * è registrato con il suo istante. Ogni valore è tipizzato sui tipi di dominio: se un evento, uno
 * stato o un tipo di messaggio sparisce o cambia nome, il typecheck e i test falliscono.
 * Chiusura (`Risolto → Chiuso`) e riapertura con `details.riapertura` arrivano con #6 e #42.
 */
exports.STATISTICS_EVENT_SOURCES = {
    /** Apertura: `ticket_events.ricevuto` (e `tickets.received_at`, stesso istante). */
    opening: { eventTypes: ['ricevuto'] },
    /**
     * Prima risposta: `ticket_messages` di tipo `risposta` con email non fallita (istante di
     * registrazione dopo l'accettazione del fornitore), oppure un'email di `chiusura` partita (#6).
     */
    firstResponse: {
        messageKinds: ['risposta'],
        emailReasons: ['chiusura'],
        excludedEmailStates: ['in_invio', 'fallita'],
    },
    /** Assegnazione ed escalation: eventi con `details.escalation` (#4). */
    assignment: {
        eventTypes: [
            'responsabile_assegnato',
            'responsabile_cambiato',
            'collaboratore_aggiunto',
        ],
    },
    /** Ogni cambio di stato: `stato_cambiato` con `details.da` e `details.a`. */
    stateChange: { eventTypes: ['stato_cambiato'] },
    /** Risoluzione, chiusura e scarto: stati d'arrivo di `stato_cambiato`. */
    resolution: { state: 'risolto' },
    closing: { state: 'chiuso' },
    discard: { state: 'scartato' },
    /**
     * Riapertura: `stato_cambiato` con `details.riapertura: true` (#6 «Riapri», #42 replica), oppure,
     * in mancanza del flag, la transizione `risolto → in_lavorazione`.
     */
    reopening: { from: 'risolto', to: 'in_lavorazione' },
};
/** Tipi di evento senza i quali le statistiche non sono calcolabili. */
exports.STATISTICS_REQUIRED_EVENT_TYPES = [
    ...exports.STATISTICS_EVENT_SOURCES.opening.eventTypes,
    ...exports.STATISTICS_EVENT_SOURCES.assignment.eventTypes,
    ...exports.STATISTICS_EVENT_SOURCES.stateChange.eventTypes,
];
//# sourceMappingURL=statistics.js.map